import React from "react";

import { TextField } from "@mui/material";
import { useField } from "formik";

import { Translate } from "@/components/Application/Translate";

type Props = {
	label: string;
	type?: string;
	name: string;
	id: string;
	rows?: number;
	placeholder?: string;
	translationString?: string;
	style?: React.CSSProperties;
	multiline?: boolean;
	fullWidth?: boolean;
	maxRows?: number;
};

/**
 * CustomTextArea is a reusable textarea component that integrates with Formik's useField hook.
 * It renders a textarea with a label and handles validation errors.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} props.label - The label text for the textarea.
 * @param {string} props.id - The id for the textarea element.
 * @param {string} [props.translationString] - The translation string for the label.
 * @param {number} [props.rows=4] - The number of rows for the textarea.
 * @param {Object} [props.style] - Additional styles for the textarea.
 * @param {boolean} [props.fullWidth] - If true, the textarea will take up the full width of its container.
 * @returns {JSX.Element} The rendered CustomTextArea component.
 */
const CustomTextArea = React.memo(
	({
		label,
		id,
		name,
		translationString,
		rows = 4,
		style,
		...props
	}: Props) => {
		const [field, meta] = useField(name);

		return (
			<div className="input-container" style={{ marginBottom: "1rem" }}>
				<label htmlFor={id}>
					<Translate String={translationString || ""}>{label}</Translate>
				</label>
				<TextField
					multiline
					rows={rows}
					id={id}
					{...field}
					{...props}
					variant="outlined"
					fullWidth={props.fullWidth}
					style={{
						...style,
						overflow: "auto",
						resize: "vertical",
						minHeight: "100px",
					}}
					InputProps={{
						sx: {
							"& .MuiInputBase-input": {
								height: "auto",
							},
						},
					}}
				/>
			</div>
		);
	},
);

export default CustomTextArea;
